import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { useAccountsContextRefresher } from "./accounts";
import { useDexContext } from "./dex";
import { useDomainsContext } from "./domains";
import { useLiquidity } from "./liquidity";
import { useMarginContext } from "./margin";
import { useMarkets } from "./market";
import { useStarAtlasContext } from "./starAtlas";

export type RefreshContextStateProps = {
  refreshing: boolean;
  refreshID: number;
};

export type RefreshContextUpdaterProps = () => Promise<void>;

const RefreshContextState = createContext<RefreshContextStateProps>({
  refreshing: false,
  refreshID: 0,
});

const RefreshContextUpdater = createContext<RefreshContextUpdaterProps>(async () => {});

export const RefreshProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [refreshing, setRefreshing] = useState(false);
  const [refreshID, setRefreshID] = useState(0);
  const refreshAccounts = useAccountsContextRefresher();
  const { refreshDomains } = useDomainsContext();
  const markets = useMarkets();
  const liquidity = useLiquidity();
  const dex = useDexContext();
  const margin = useMarginContext();
  const { refreshContext: refreshSAContext } = useStarAtlasContext();

  const refreshContexts = useCallback(async () => {
    setRefreshing(true);
    setRefreshID((refreshID) => refreshID + 1);
    await Promise.all([
      liquidity.refreshPools(),
      refreshAccounts(),
      refreshDomains(),
      markets.refreshPrices(),
      margin.refreshContext(),
    ]);
    refreshSAContext();
    dex.refreshContext();
    setRefreshing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextState = useMemo(() => ({ refreshing, refreshID }), [refreshing, refreshID]);

  return (
    <RefreshContextState.Provider value={contextState}>
      <RefreshContextUpdater.Provider value={refreshContexts}>{children}</RefreshContextUpdater.Provider>
    </RefreshContextState.Provider>
  );
};

export const useRefreshContext = () => {
  const context = useContext(RefreshContextState);
  return context;
};

export const useRefreshUpdateContext = () => {
  const context = useContext(RefreshContextUpdater);
  return context;
};
